/**
 * 框架通道收单前置判定（ADM-PAY-S008 切换收单方 / 编辑策略组、ADM-PAY-S001 支付页渠道卡共用）。
 *
 * 把策略组成员切到框架通道（如 PayerMax）或给它配 placement，写侧都不做 serving 校验；但该 App 在该通道的配置
 * 未就绪时，C 端 discovery 不下发这个通道的支付方式。本判定只读 `GET /integrations/payment-providers/:channel/configs`
 * （A8 §10.1，viewer+），按 `app_id` 取本 App 的配置行，供页面以警示语气提示、不阻断写入。
 *
 * 判据取字典 `03-数据字典/65-三方支付通道.md` §1「可售与发起」中后台读得到的部分：
 *  - 无本 App 配置行 → `missing`
 *  - `is_active=false`、`connection_status≠connected`、原生 App 的 `native_return_base_url` 为 NULL，任一命中
 *    → `not_ready`（逐项带出，文案逐项说明）
 *  - 以上都满足 → `ready_readable`：只代表**后台读得到的条件**满足；回跳 host 是否仍在当前环境登记、部署环境是否
 *    放行该通道由 C 端判定、后台读不到，调用方不得把它呈现成「一定会下发」。
 * 读取失败由调用方按「无法判定」处理（不在本函数里猜）。
 */

import {
  PAYMENT_PROVIDER_CHANNELS,
  type PaymentProviderChannel,
  type PaymentProviderConfigResponse,
  type PaymentProviderConnectionStatus,
} from '@novel-admin/shared';

export type AcquiringPrerequisite =
  | { status: 'ready_readable' }
  | { status: 'missing' }
  | {
      status: 'not_ready';
      isActive: boolean;
      connectionStatus: PaymentProviderConnectionStatus;
      /** 原生 App 缺 `native_return_base_url`（Web App 恒 false） */
      nativeReturnMissing: boolean;
    };

/** 该结算通道是否由「框架通道配置」承载收单（是才需要判收单前置；IAP / STRIPE 不在此列）。 */
export function isPaymentProviderChannel(channel: string): channel is PaymentProviderChannel {
  return (PAYMENT_PROVIDER_CHANNELS as readonly string[]).includes(channel);
}

export function acquiringPrerequisiteOf(
  configs: readonly PaymentProviderConfigResponse[],
  appId: string,
  opts: { isNativeApp: boolean },
): AcquiringPrerequisite {
  const config = configs.find((row) => row.app_id === appId);
  if (!config) return { status: 'missing' };
  const nativeReturnMissing = opts.isNativeApp && config.native_return_base_url === null;
  if (config.is_active && config.connection_status === 'connected' && !nativeReturnMissing) {
    return { status: 'ready_readable' };
  }
  return {
    status: 'not_ready',
    isActive: config.is_active,
    connectionStatus: config.connection_status,
    nativeReturnMissing,
  };
}

/** 连接状态（字典 65 §1 `connection_status`）→ 运营可读文案 */
export const CONNECTION_STATUS_TEXT: Record<PaymentProviderConnectionStatus, string> = {
  connected: '已连接',
  unverified: '未验证连接',
  invalid: '连接已失效',
};

/**
 * 未就绪的现状描述（如「未启用（已连接）」「连接已失效、缺少原生回跳地址」）；`ready_readable` 返 null。
 * 只有「未启用」一项时按设计稿带出连接现状，便于运营判断是启用问题还是连接问题。
 */
export function describeAcquiringPrerequisite(prerequisite: AcquiringPrerequisite): string | null {
  if (prerequisite.status === 'ready_readable') return null;
  if (prerequisite.status === 'missing') return '不存在（尚未配置）';
  const problems = [
    ...(prerequisite.isActive ? [] : ['未启用']),
    ...(prerequisite.connectionStatus === 'connected'
      ? []
      : [CONNECTION_STATUS_TEXT[prerequisite.connectionStatus]]),
    ...(prerequisite.nativeReturnMissing ? ['缺少原生回跳地址'] : []),
  ];
  if (problems.length === 1 && !prerequisite.isActive) return '未启用（已连接）';
  return problems.join('、');
}
